/**
 * What counts as a USER-ACTIONABLE problem (1.67.x).
 *
 * "Action needed" on the home screen must mean: *this person can fix it right
 * now*. It must never mean "the system hit an internal problem".
 *
 * Everything the pipeline recovers from on its own — pending uploads, retries,
 * backoff, offline waits, background PDF/invoice/email work — is silent.
 * Everything the pipeline cannot recover from but the DRIVER cannot fix either
 * (RLS/authorisation denials, invalid ownership data, corrupt durable bytes,
 * verification failures) is an INTERNAL exception: it stays fully observable in
 * telemetry and support tooling, but it is never shown to the end user.
 *
 * The only photo-pipeline failure a user can genuinely resolve is running out
 * of device storage: they can free up space and the existing retry path then
 * succeeds. Nothing is deleted, marked uploaded, or bypassed either way.
 */
import type { PhotoFailureKind } from "@/lib/photoFailureKinds";

/** Failure kinds the user themselves can resolve. Deliberately tiny. */
const USER_ACTIONABLE_KINDS: ReadonlySet<string> = new Set<PhotoFailureKind>([
  "durable_storage_quota_exceeded",
]);

export function isUserActionableFailureKind(kind: unknown): boolean {
  return typeof kind === "string" && USER_ACTIONABLE_KINDS.has(kind);
}

/**
 * Internal exceptions are real and must stay observable — they are simply not
 * the end user's problem to solve.
 */
export function isInternalOnlyFailureKind(kind: unknown): boolean {
  return typeof kind === "string" && kind.length > 0 && !isUserActionableFailureKind(kind);
}
